import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const READ_BLOCK = 64
const WRITE_BLOCK = 32
const MEMORY_SIZE = 0x1c40
const WRITE_END = 0x1c20
const CHANNEL_COUNT = 128
const NAME_START = 0x0c00
const NARROW_MASK = 0x02
const SCAN_MASK = 0x20
const AIRBAND = [108_000_000, 136_000_000] as const

type GA510Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: string
  powerLevels: readonly string[]
  airband?: boolean
}

export type GA510Profile = {
  protocol: 'ga510'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 0
  channelMaximum: 127
  nameLength: 10
  memorySize: number
  magic: readonly number[]
  powerLevels: readonly string[]
  airband: boolean
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

const SPECS: readonly GA510Spec[] = [
  {
    id: 'radioddity-ga-510',
    vendor: 'Radioddity',
    model: 'GA-510',
    summary: 'Radioddity GA-510 128-channel commercial handheld.',
    magic: 'PROGROMBFHU',
    powerLevels: ['High', 'Low', 'Mid'],
  },
  {
    id: 'retevis-ra685',
    vendor: 'Retevis',
    model: 'RA685',
    summary: 'Retevis RA685 128-channel commercial handheld.',
    magic: 'PROGROMWLTU',
    powerLevels: ['High', 'Low', 'Mid'],
  },
  {
    id: 'retevis-ra85',
    vendor: 'Retevis',
    model: 'RA85',
    summary: 'Retevis RA85 128-channel commercial handheld.',
    magic: 'PROGROMWLTU',
    powerLevels: ['High', 'Low', 'Mid'],
  },
  {
    id: 'tidradio-td-h6',
    vendor: 'TIDRADIO',
    model: 'TD-H6',
    summary: 'TIDRADIO TD-H6 128-channel commercial handheld.',
    magic: 'PROGROMBFHU',
    powerLevels: ['High', 'Low', 'Mid'],
  },
  {
    id: 'senhaix-8800',
    vendor: 'SenhaiX',
    model: '8800',
    summary: 'SenhaiX 8800 commercial handheld with AM airband receive.',
    magic: 'PROGROMSHXU',
    powerLevels: ['High', 'Low'],
    airband: true,
  },
  {
    id: 'radioddity-gs-5b',
    vendor: 'Radioddity',
    model: 'GS-5B',
    summary: 'Radioddity GS-5B commercial handheld with AM airband receive.',
    magic: 'PROGROMSHXU',
    powerLevels: ['High', 'Low'],
    airband: true,
  },
  {
    id: 'cignus-xtr-5',
    vendor: 'Cignus',
    model: 'XTR-5',
    summary: 'Cignus XTR-5 commercial handheld with AM airband receive.',
    magic: 'PROGROMSHXU',
    powerLevels: ['High', 'Low'],
    airband: true,
  },
  {
    id: 'anysecu-ac-580',
    vendor: 'Anysecu',
    model: 'AC-580',
    summary: 'Anysecu AC-580 commercial handheld with AM airband receive.',
    magic: 'PROGROMSHXU',
    powerLevels: ['High', 'Low'],
    airband: true,
  },
  {
    id: 'abbree-ar-f5',
    vendor: 'Abbree',
    model: 'AR-F5',
    summary: 'Abbree AR-F5 128-channel commercial handheld.',
    magic: 'PROGROMWLTU',
    powerLevels: ['High', 'Low', 'Mid'],
  },
]

export const GA510_PROFILES: readonly GA510Profile[] = SPECS.map((spec) => ({
  protocol: 'ga510',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '128 channels', '10-character names'],
  baudRate: 9600,
  channelMinimum: 0,
  channelMaximum: 127,
  nameLength: 10,
  memorySize: MEMORY_SIZE,
  magic: ascii(spec.magic),
  powerLevels: spec.powerLevels,
  airband: spec.airband ?? false,
}))

function emptyChannel(number: number, powerLevels: readonly string[]): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: powerLevels[0],
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value < 670) {
    const encoded = value - 1
    const index = encoded % DTCS_CODES.length
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: DTCS_CODES[index] ?? null,
      polarity: index === encoded ? 'N' : 'R',
    }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    value = index + 1 + (channel.dtcsPolarity === 'R' ? DTCS_CODES.length : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function powerFromCode(code: number, powerLevels: readonly string[]): string {
  if (powerLevels.length === 2) {
    return code === 1 ? powerLevels[1] : powerLevels[0]
  }
  return code === 1
    ? powerLevels[1]
    : code === 2
      ? powerLevels[2]
      : powerLevels[0]
}

function codeFromPower(power: string, powerLevels: readonly string[]): number {
  return Math.max(0, powerLevels.indexOf(power))
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value < 0x20 || value >= 0x7f ? 0x20 : value)),
  ).trim()
}

export function parseGA510Channel(
  image: Uint8Array,
  profile: GA510Profile,
  number: number,
): WebChannel {
  const offset = number * 16
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff) {
    return emptyChannel(number, profile.powerLevels)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number, profile.powerLevels)
  const txFrequency =
    raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const am =
    profile.airband &&
    rxFrequency >= AIRBAND[0] &&
    rxFrequency < AIRBAND[1]
  return {
    number,
    empty: false,
    name: decodeName(
      image.slice(NAME_START + number * 16, NAME_START + number * 16 + 10),
    ),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: am ? 'AM' : raw[15] & NARROW_MASK ? 'NFM' : 'FM',
    power: powerFromCode((raw[14] >> 6) & 0x03, profile.powerLevels),
    skip: (raw[15] & SCAN_MASK) === 0,
  }
}

export function parseGA510Channels(
  image: Uint8Array,
  profile: GA510Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, number) => parseGA510Channel(image, profile, number),
  )
}

export function applyGA510Channel(
  image: Uint8Array,
  profile: GA510Profile,
  channel: WebChannel,
): void {
  if (channel.number < 0 || channel.number >= CHANNEL_COUNT) {
    throw new Error('Channel must be between 0 and 127.')
  }
  const offset = channel.number * 16
  const raw = image.subarray(offset, offset + 16)
  const nameStart = NAME_START + channel.number * 16
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameStart, nameStart + 16)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[14] =
    (raw[14] & 0x3f) |
    ((codeFromPower(channel.power, profile.powerLevels) & 0x03) << 6)
  raw[15] =
    (raw[15] & ~(NARROW_MASK | SCAN_MASK)) |
    (channel.mode === 'NFM' || channel.mode === 'AM' ? NARROW_MASK : 0) |
    (channel.skip ? 0 : SCAN_MASK)
  if (profile.airband) {
    raw[15] =
      channel.txFrequency === null
        ? raw[15] & ~0x40
        : raw[15] | 0x40
  }
  const name = channel.name.slice(0, 10).padEnd(10, ' ')
  image.fill(0xff, nameStart, nameStart + 16)
  for (let index = 0; index < name.length; index += 1) {
    image[nameStart + index] = name.charCodeAt(index) & 0xff
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: GA510Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  await readAck(transport, 'The radio refused GA-510 programming mode.')
  await transport.write(new TextEncoder().encode('F'))
  await transport.readExact(8, 3000)
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, READ_BLOCK),
  )
  const response = await transport.readExact(4 + READ_BLOCK, 4000)
  if (
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== READ_BLOCK
  ) {
    throw new Error(`Unexpected GA-510 read reply at 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + WRITE_BLOCK)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    WRITE_BLOCK,
  ])
  frame.set(data, 4)
  await transport.write(frame)
  await readAck(
    transport,
    `The radio rejected GA-510 write block 0x${address.toString(16)}.`,
  )
}

export async function readGA510RadioWithTransport(
  profile: GA510Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / READ_BLOCK
    for (let index = 0; index < total; index += 1) {
      const address = index * READ_BLOCK
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading GA-510 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeGA510RadioWithTransport(
  profile: GA510Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const total = WRITE_END / WRITE_BLOCK
    for (let index = 0; index < total; index += 1) {
      const address = index * WRITE_BLOCK
      await writeBlock(
        transport,
        address,
        image.slice(address, address + WRITE_BLOCK),
      )
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing GA-510 memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
